/**
 * Lazy-loaded components with intelligent preloading
 */

import React, { Suspense } from 'react';
import { createLazyComponent, useHoverPreload, useViewportPreload } from '../utils/lazyLoading';
import LoadingSpinner from './LoadingSpinner';

// Enhanced loading fallback component
const EnhancedLoadingFallback: React.FC<{ 
  message?: string; 
  size?: 'small' | 'medium' | 'large';
  showProgress?: boolean;
}> = ({ 
  message = 'Loading...', 
  size = 'medium',
  showProgress = false 
}) => {
  const sizeClasses = {
    small: 'h-20',
    medium: 'h-40',
    large: 'h-60'
  };

  return (
    <div className={`flex flex-col items-center justify-center ${sizeClasses[size]} p-4`}>
      <LoadingSpinner size={size === 'small' ? 'sm' : size === 'large' ? 'lg' : 'md'} />
      <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">{message}</p>
      {showProgress && (
        <div className="mt-2 w-32 h-1 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
          <div className="h-full bg-blue-500 animate-pulse"></div>
        </div>
      )}
    </div>
  );
};

// Error boundary for lazy components
class LazyComponentErrorBoundary extends React.Component<
  { children: React.ReactNode; fallback?: React.ComponentType },
  { hasError: boolean }
> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Lazy component loading error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      const FallbackComponent = this.props.fallback;
      if (FallbackComponent) {
        return <FallbackComponent />;
      }
      
      return (
        <div className="flex flex-col items-center justify-center h-40 p-4 border border-red-200 rounded-lg bg-red-50 dark:bg-red-900/20 dark:border-red-800">
          <div className="text-red-600 dark:text-red-400 text-center">
            <svg className="w-8 h-8 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <p className="text-sm font-medium">Failed to load component</p>
            <button 
              onClick={() => window.location.reload()} 
              className="mt-2 text-xs underline hover:no-underline"
            >
              Refresh page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Wrapper component for lazy loading with preloading strategies
const LazyWrapper: React.FC<{
  children: React.ReactNode;
  chunkName?: string;
  preloadOnHover?: boolean;
  preloadOnViewport?: boolean;
  fallback?: React.ReactNode;
  errorFallback?: React.ComponentType;
}> = ({ 
  children, 
  chunkName, 
  preloadOnHover = false, 
  preloadOnViewport = false,
  fallback,
  errorFallback
}) => {
  const hoverProps = preloadOnHover && chunkName ? useHoverPreload(chunkName) : {};
  const viewportProps = preloadOnViewport && chunkName ? useViewportPreload(chunkName) : {};

  return (
    <div {...hoverProps} {...viewportProps}>
      <LazyComponentErrorBoundary fallback={errorFallback}>
        <Suspense fallback={fallback || <EnhancedLoadingFallback />}>
          {children}
        </Suspense>
      </LazyComponentErrorBoundary>
    </div>
  );
};

// Lazy-loaded page components with intelligent preloading
export const LazyHomePage = createLazyComponent(
  () => import('../pages/HomePage'),
  { 
    preloadStrategy: 'immediate', 
    priority: 'high',
    chunkName: 'home-page'
  }
);

export const LazyCameraPage = createLazyComponent(
  () => import('../pages/CameraPage'),
  { 
    preloadStrategy: 'hover', 
    priority: 'high',
    chunkName: 'camera-page'
  }
);

export const LazyHistoryPage = createLazyComponent(
  () => import('../pages/HistoryPage'),
  { 
    preloadStrategy: 'viewport', 
    priority: 'medium',
    chunkName: 'history-page'
  }
);

export const LazyFavoritesPage = createLazyComponent(
  () => import('../pages/FavoritesPage'),
  { 
    preloadStrategy: 'viewport', 
    priority: 'medium',
    chunkName: 'favorites-page'
  }
);

export const LazyProfilePage = createLazyComponent(
  () => import('../pages/ProfilePage'),
  { 
    preloadStrategy: 'idle', 
    priority: 'low',
    chunkName: 'profile-page'
  }
);

// Analysis page components (heavy components with conservative loading)
export const LazyAnalysisSelectionPage = createLazyComponent(
  () => import('../pages/analysis/ThriftalyzeAnalyzePage'),
  { 
    preloadStrategy: 'hover', 
    priority: 'medium',
    chunkName: 'thriftalyze-analyze'
  }
);

export const LazyAnalyzeGeminiPage = createLazyComponent(
  () => import('../pages/analysis/AnalyzeGeminiPage'),
  { 
    preloadStrategy: 'never', 
    priority: 'low',
    chunkName: 'analyze-gemini',
    timeout: 15000,
    retries: 2
  }
);

export const LazyAnalyzeGrokPage = createLazyComponent(
  () => import('../pages/analysis/AnalyzeGrokPage'),
  { 
    preloadStrategy: 'never', 
    priority: 'low',
    chunkName: 'analyze-grok',
    timeout: 15000,
    retries: 2
  }
);

export const LazyAnalyzeSerpAPIPage = createLazyComponent(
  () => import('../pages/analysis/AnalyzeSerpAPIPage'),
  { 
    preloadStrategy: 'never', 
    priority: 'low',
    chunkName: 'analyze-serpapi',
    timeout: 15000,
    retries: 2
  }
);

export const LazyAnalyzeSearchAPIPage = createLazyComponent(
  () => import('../pages/analysis/AnalyzeSearchAPIPage'),
  { 
    preloadStrategy: 'never', 
    priority: 'low',
    chunkName: 'analyze-searchapi',
    timeout: 15000,
    retries: 2
  }
);

export const LazyAnalyzeDecodoPage = createLazyComponent(
  () => import('../pages/analysis/AnalyzeDecodoPage'),
  { 
    preloadStrategy: 'never', 
    priority: 'low',
    chunkName: 'analyze-decodo',
    timeout: 15000,
    retries: 2
  }
);

export const LazyAnalyzeOpenLensPage = createLazyComponent(
  () => import('../pages/analysis/AnalyzeOpenLensPage'),
  { 
    preloadStrategy: 'never', 
    priority: 'low',
    chunkName: 'analyze-openlens',
    timeout: 15000,
    retries: 2
  }
);

// Auth page components
export const LazySignInPage = createLazyComponent(
  () => import('../pages/auth/SignInPage'),
  { 
    preloadStrategy: 'hover', 
    priority: 'medium',
    chunkName: 'signin-page'
  }
);

export const LazySignUpPage = createLazyComponent(
  () => import('../pages/auth/SignUpPage'),
  { 
    preloadStrategy: 'hover', 
    priority: 'medium',
    chunkName: 'signup-page'
  }
);

export const LazyResetPasswordPage = createLazyComponent(
  () => import('../pages/auth/ResetPasswordPage'),
  { 
    preloadStrategy: 'never', 
    priority: 'low',
    chunkName: 'reset-password-page'
  }
);

export const LazyWelcomePage = createLazyComponent(
  () => import('../pages/auth/WelcomePage'),
  { 
    preloadStrategy: 'immediate', 
    priority: 'high',
    chunkName: 'welcome-page'
  }
);

export const LazyAuthCallbackPage = createLazyComponent(
  () => import('../pages/auth/AuthCallbackPage'),
  { 
    preloadStrategy: 'never', 
    priority: 'medium',
    chunkName: 'auth-callback-page'
  }
);

export const LazyEmailConfirmationPage = createLazyComponent(
  () => import('../pages/auth/EmailConfirmationPage'),
  { 
    preloadStrategy: 'never', 
    priority: 'low',
    chunkName: 'email-confirmation-page'
  }
);

// Admin and utility pages (low priority)
export const LazyTroubleshootingPage = createLazyComponent(
  () => import('../pages/TroubleshootingPage'),
  { 
    preloadStrategy: 'never', 
    priority: 'low',
    chunkName: 'troubleshooting-page'
  }
);

export const LazyImageEnhancementDemo = createLazyComponent(
  () => import('../pages/ImageEnhancementDemo'),
  { 
    preloadStrategy: 'never', 
    priority: 'low',
    chunkName: 'image-enhancement-demo'
  }
);

export const LazyAccountSettingsPage = createLazyComponent(
  () => import('../pages/AccountSettingsPage'),
  { 
    preloadStrategy: 'idle', 
    priority: 'low',
    chunkName: 'account-settings-page'
  }
);

export const LazyAdminPanel = createLazyComponent(
  () => import('../pages/AdminPanel'),
  { 
    preloadStrategy: 'never', 
    priority: 'low',
    chunkName: 'admin-panel'
  }
);

export const LazyAnalyticsPage = createLazyComponent(
  () => import('../pages/AnalyticsPage'),
  { 
    preloadStrategy: 'idle', 
    priority: 'low',
    chunkName: 'analytics-page'
  }
);

export const LazyDatabaseTestPage = createLazyComponent(
  () => import('../pages/DatabaseTest'),
  { 
    preloadStrategy: 'never', 
    priority: 'low',
    chunkName: 'database-test-page'
  }
);

export const LazyLiveVideoAnalysisPage = createLazyComponent(
  () => import('../pages/LiveVideoAnalysisPage'),
  { 
    preloadStrategy: 'never', 
    priority: 'low',
    chunkName: 'live-video-analysis-page'
  }
);

export const LazyLoadingDemo = createLazyComponent(
  () => import('../pages/LoadingDemo'),
  { 
    preloadStrategy: 'never', 
    priority: 'low',
    chunkName: 'loading-demo-page'
  }
);

// Inventory page components
export const LazyItemListingPage = createLazyComponent(
  () => import('../pages/ItemListingPage'),
  { 
    preloadStrategy: 'hover', 
    priority: 'medium',
    chunkName: 'item-listing-page'
  }
);

export const LazyInventoryPage = createLazyComponent(
  () => import('../pages/InventoryPage'),
  { 
    preloadStrategy: 'idle', 
    priority: 'medium',
    chunkName: 'inventory-page'
  }
);

// Export the wrapper component for custom lazy loading
export { LazyWrapper, EnhancedLoadingFallback };
